import { CONVOY_HEIGHTS, type ConvoyConfig, type ConvoySummary } from "@air-defence-sim/sim-core";

const fmt1 = (x: number): string => x.toFixed(1);

/** Build the "Reading this run" commentary HTML for the analysis panel. */
export function buildRunAnalysis(cfg: ConvoyConfig, s: ConvoySummary): string {
  const H = CONVOY_HEIGHTS;
  const horizon = cfg.standoff / cfg.dspeed;
  const expectedShots = Math.ceil(1 / cfg.pk);
  const tKill = cfg.react + (expectedShots - 1) * 0.9;
  const preCued = cfg.topCover === "uav" || cfg.topCover === "armed";
  const mastOverCanopy = H.mastSensor > cfg.canopy;
  const mastSkirt = mastOverCanopy
    ? (H.mastSensor - H.droneCruise) / (H.mastSensor - cfg.canopy)
    : null;
  const owSkirt = (H.overwatchAlt - H.droneCruise) / (H.overwatchAlt - cfg.canopy);
  const parts: string[] = [];
  parts.push(`<div class="figure"><b>The sensor horizon.</b> With <b>${cfg.standoff} m</b> of open ground and drones
    at <b>${cfg.dspeed} m/s</b>, a defender that first sees the threat at the treeline has about
    <b>${fmt1(horizon)} s</b>. The hard-kill chain needs <b>${cfg.react} s</b> to react plus 0.9 s per follow-up
    shot; at Pk ${cfg.pk} the expected kill takes ~<b>${fmt1(tKill)} s</b> per drone.
    ${
      horizon < tKill
        ? "That window is <b>shorter than the kill chain</b> — every kill in this run came from a launcher further up the column, not the targeted vehicle."
        : "The window covers roughly one engagement per defender per crossing drone — with nothing to spare."
    }
    ${
      preCued
        ? "The overwatch sensor catches the launch climb over the clearing, so tracks exist before the drones break the treeline — that pre-loads the picture, but the firing solution still has to wait for line of sight."
        : ""
    }</div>`);
  parts.push(`<div class="figure"><b>Reading the dead ground.</b> Canopy here is <b>${cfg.canopy} m</b>; a hull
    sensor sits at <b>${H.hullSensor} m</b>, so every treeline is a wall — the grey wash on the map is the area
    where a drone at <b>${H.droneCruise} m</b> is invisible to <em>every</em> vehicle in the column. Each launch
    starts with a ~${fmt1(H.droneClimbTime)} s climb above the canopy: elevated sensors get that cue, hull
    sensors don't.
    ${
      cfg.topCover === "mast"
        ? mastOverCanopy
          ? `The <b>${H.mastSensor} m</b> mast head clears this canopy, turning the wall into a finite skirt:
             dead ground behind a treeline at distance <em>d</em> ends at about <b>${fmt1(mastSkirt ?? 0)}×d</b>.`
          : `The <b>${H.mastSensor} m</b> mast head is <em>below</em> this canopy — it still catches launch
             climbs, but the treeline stays a wall.`
        : ""
    }
    ${
      preCued
        ? `Overwatch at <b>${H.overwatchAlt} m</b> has a skirt multiplier of just <b>${fmt1(owSkirt)}×</b> —
           effectively no dead ground at all. The green wash shows the only places hidden from it: the
           under-canopy interiors of the woods themselves.`
        : `An overwatch platform at ${H.overwatchAlt} m would shrink that whole wash to a skirt multiplier of
           <b>${fmt1(owSkirt)}×</b> — effectively nothing but the woods' own interiors. That asymmetry is the
           argument for elevation.`
    }</div>`);
  parts.push(`<p>This run: <span class="k">${s.launched} launched</span> &rarr;
    <span class="k">${s.killedV} killed by the convoy</span>${
      s.killedO ? ` + <span class="k">${s.killedO} by overwatch</span>` : ""
    },
    <span class="k">${s.leakers} leaker${s.leakers === 1 ? "" : "s"}</span>, costing
    <span class="k">${s.lost} vehicle${s.lost === 1 ? "" : "s"}</span>.
    Worst-case warning was <span class="k">${fmt1(s.warnMin)} s</span>.
    ${
      s.sat
        ? `The saturation counter logged <span class="k">${s.sat}</span> moment${
            s.sat === 1 ? "" : "s"
          } where a tracked drone was inside engagement range with <em>no free fire channel anywhere in the column</em> — those are the attacks no amount of Pk fixes.`
        : "No saturation events — the column always had a free fire channel. The attacker under-resourced this one."
    }
    ${
      cfg.axes === 2
        ? "The two-axis split means each side of the road could only be serviced by the launchers with line of sight to it — roughly half the column at any moment."
        : ""
    }</p>`);
  return parts.join("");
}

/** Build the counterfactual comparison figure. `cs` is the same seed re-run with armed overwatch. */
export function buildCounterfactual(s: ConvoySummary, cs: ConvoySummary | null): string {
  if (!cs) {
    return `<b>This run already has armed overwatch.</b> Switch top cover to
      <em>None</em> and re-run the same seed to see what the convoy looks like without it.`;
  }
  const dl = s.leakers - cs.leakers;
  const dv = s.lost - cs.lost;
  return `<b>Counterfactual, same seed:</b> replaying this exact attack with an armed
    overwatch layer gives <b>${cs.leakers} leaker${cs.leakers === 1 ? "" : "s"}</b> and
    <b>${cs.lost} vehicle${cs.lost === 1 ? "" : "s"} lost</b>
    (vs ${s.leakers} and ${s.lost} without) — overwatch killed <b>${cs.killedO}</b> drones over the canopy,
    before the convoy's own systems ever had line of sight.
    ${
      dl > 0 || dv > 0
        ? `That is <b>${dl > 0 ? `${dl} fewer leaker${dl === 1 ? "" : "s"}` : ""}${
            dl > 0 && dv > 0 ? " and " : ""
          }${dv > 0 ? `${dv} vehicle${dv === 1 ? "" : "s"} saved` : ""}</b> from the same attack, same dice.`
        : "This attack was survivable either way — push the sliders toward the swarm preset and run it again."
    }`;
}
